'use client';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { QUIZ_QUESTIONS, QuizQuestion, PASS_MARK, TOTAL_QUESTIONS } from '../data/questions';
import QuizHeader from './QuizHeader';
import AnswerCard from './AnswerCard';
import ExplanationPanel from './ExplanationPanel';

type AnswerState = 'default' | 'selected' | 'correct' | 'incorrect' | 'disabled';

interface AnswerRecord {
  questionId: string;
  selectedOptionId: string;
  isCorrect: boolean;
  category: string;
}

// ── NEW: Candidate form ────────────────────────────────────────────────────────
interface CandidateFormProps {
  onSubmit: (firstName: string, lastName: string) => void;
}

function CandidateForm({ onSubmit }: CandidateFormProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [errors, setErrors] = useState<{ firstName?: string; lastName?: string }>({});

  function validate() {
    const newErrors: { firstName?: string; lastName?: string } = {};
    if (!firstName.trim()) newErrors.firstName = 'First name is required.';
    if (!lastName.trim()) newErrors.lastName = 'Last name is required.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (validate()) {
      onSubmit(firstName.trim(), lastName.trim());
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#F8F9FB' }}>
      <div
        className="max-w-lg w-full rounded-3xl p-8 sm:p-10 fade-in-up"
        style={{
          background: '#FFFFFF',
          border: '1px solid #E2E8F0',
          boxShadow: '0 8px 40px rgba(15,23,42,0.10)',
          opacity: 0,
          animationFillMode: 'forwards',
        }}
      >
        {/* Icon */}
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto"
          style={{ background: 'rgba(30,64,175,0.08)', border: '1px solid rgba(30,64,175,0.15)' }}
          aria-hidden="true"
        >
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <circle cx="16" cy="12" r="5" stroke="#1E40AF" strokeWidth="1.5" fill="rgba(30,64,175,0.1)" />
            <path d="M6 26c0-5.523 4.477-10 10-10s10 4.477 10 10" stroke="#1E40AF" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </div>

        <h1 className="text-2xl font-bold text-center mb-2" style={{ color: '#0F172A' }}>
          Candidate Details
        </h1>
        <p className="text-sm text-center mb-8" style={{ color: '#64748B' }}>
          Please enter your name before starting the quiz.
        </p>

        <form onSubmit={handleSubmit} noValidate>
          {/* First Name */}
          <div className="mb-5">
            <label
              htmlFor="firstName"
              className="block text-sm font-semibold mb-1.5"
              style={{ color: '#0F172A' }}
            >
              First Name <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <input
              id="firstName"
              type="text"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                if (errors.firstName) setErrors((prev) => ({ ...prev, firstName: undefined }));
              }}
              placeholder="e.g. John"
              className="w-full rounded-xl px-4 py-3 text-sm outline-none transition-all duration-200"
              style={{
                border: errors.firstName ? '1.5px solid #DC2626' : '1.5px solid #E2E8F0',
                background: '#F8F9FB',
                color: '#0F172A',
              }}
              aria-required="true"
              aria-describedby={errors.firstName ? 'firstName-error' : undefined}
              autoComplete="given-name"
            />
            {errors.firstName && (
              <p id="firstName-error" className="mt-1.5 text-xs font-medium" style={{ color: '#DC2626' }} role="alert">
                {errors.firstName}
              </p>
            )}
          </div>

          {/* Last Name */}
          <div className="mb-6">
            <label
              htmlFor="lastName"
              className="block text-sm font-semibold mb-1.5"
              style={{ color: '#0F172A' }}
            >
              Last Name <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <input
              id="lastName"
              type="text"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                if (errors.lastName) setErrors((prev) => ({ ...prev, lastName: undefined }));
              }}
              placeholder="e.g. Smith"
              className="w-full rounded-xl px-4 py-3 text-sm outline-none transition-all duration-200"
              style={{
                border: errors.lastName ? '1.5px solid #DC2626' : '1.5px solid #E2E8F0',
                background: '#F8F9FB',
                color: '#0F172A',
              }}
              aria-required="true"
              aria-describedby={errors.lastName ? 'lastName-error' : undefined}
              autoComplete="family-name"
            />
            {errors.lastName && (
              <p id="lastName-error" className="mt-1.5 text-xs font-medium" style={{ color: '#DC2626' }} role="alert">
                {errors.lastName}
              </p>
            )}
          </div>

          <button
            type="submit"
            className="btn-primary w-full text-sm py-4 rounded-xl"
            style={{ boxShadow: '0 4px 20px rgba(30,64,175,0.35)' }}
          >
            Continue to Quiz
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </form>

        {/* Privacy note */}
        <p className="mt-5 text-xs text-center leading-relaxed" style={{ color: '#94A3B8' }}>
          🔒 Your name will only be used to display your quiz result and generate your PDF report.
        </p>
      </div>
    </div>
  );
}
// ── END CandidateForm ──────────────────────────────────────────────────────────

const OPTION_LABELS = ['A', 'B', 'C', 'D'];
const MILESTONES = [10, 20, 30, 40, 50];

function MilestoneBanner({ milestone, onDismiss }: { milestone: number; onDismiss: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 2800);
    return () => clearTimeout(t);
  }, [onDismiss]);

  const isComplete = milestone === 50;

  return (
    <div
      className="fixed inset-x-0 top-20 z-50 flex justify-center pointer-events-none"
      aria-live="polite"
      aria-atomic="true"
    >
      <div
        className="flex items-center gap-3 px-6 py-3.5 rounded-2xl shadow-xl fade-in-up pointer-events-auto"
        style={{
          background: isComplete
            ? 'linear-gradient(135deg, #0B1628 0%, #1A2744 100%)'
            : 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
          border: '1px solid rgba(255,255,255,0.15)',
          animationFillMode: 'forwards',
          opacity: 0,
        }}
      >
        <span className="text-xl" aria-hidden="true">{isComplete ? '🏆' : '🎯'}</span>
        <div>
          <p className="text-white font-bold text-sm">
            {isComplete ? 'Quiz Complete!' : `${milestone} Questions Done!`}
          </p>
          <p className="text-xs" style={{ color: 'rgba(203,213,225,0.8)' }}>
            {isComplete ? 'Calculating your results…' : `${TOTAL_QUESTIONS - milestone} questions remaining`}
          </p>
        </div>
      </div>
    </div>
  );
}

function QuizIntro({ onStart }: { onStart: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#F8F9FB' }}>
      <div
        className="max-w-lg w-full rounded-3xl p-8 sm:p-10 fade-in-up"
        style={{
          background: '#FFFFFF',
          border: '1px solid #E2E8F0',
          boxShadow: '0 8px 40px rgba(15,23,42,0.10)',
          opacity: 0,
          animationFillMode: 'forwards',
        }}
      >
        {/* Icon */}
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto"
          style={{ background: 'rgba(30,64,175,0.08)', border: '1px solid rgba(30,64,175,0.15)' }}
          aria-hidden="true"
        >
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <path d="M16 4L4 9v10c0 7 5 12 12 13 7-1 12-6 12-13V9L16 4z" fill="rgba(30,64,175,0.12)" stroke="#1E40AF" strokeWidth="1.5" />
            <path d="M11 16l3.5 3.5 6.5-7" stroke="#1E40AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        <h1 className="text-2xl font-bold text-center mb-2" style={{ color: '#0F172A' }}>
          UK Driving Theory Quiz
        </h1>
        <p className="text-sm text-center mb-8" style={{ color: '#64748B' }}>
          Official DVSA-style assessment — 50 questions
        </p>

        {/* Stats grid */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          {[
            { label: 'Questions', value: '50', icon: '📋' },
            { label: 'Pass Mark', value: `${PASS_MARK}/50`, icon: '✅' },
            { label: 'Categories', value: '15', icon: '📚' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="rounded-xl p-3 text-center"
              style={{ background: '#F8F9FB', border: '1px solid #E2E8F0' }}
            >
              <div className="text-lg mb-1" aria-hidden="true">{stat.icon}</div>
              <div className="font-mono-nums font-bold text-base" style={{ color: '#0F172A' }}>{stat.value}</div>
              <div className="text-xs" style={{ color: '#94A3B8' }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Pass mark info */}
        <div
          className="rounded-xl p-4 mb-6 flex items-start gap-3"
          style={{ background: 'rgba(30,64,175,0.05)', border: '1px solid rgba(30,64,175,0.15)' }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="shrink-0 mt-0.5" aria-hidden="true">
            <circle cx="9" cy="9" r="8" stroke="#1E40AF" strokeWidth="1.3" />
            <path d="M9 6v.5M9 9v4" stroke="#1E40AF" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <div>
            <p className="text-sm font-semibold mb-1" style={{ color: '#1E40AF' }}>
              Pass Mark: {PASS_MARK}/50
            </p>
            <p className="text-xs leading-relaxed" style={{ color: '#475569' }}>
              You need at least <strong>{PASS_MARK} correct answers out of 50</strong> to pass. This matches the official DVSA theory test standard (86%).
            </p>
          </div>
        </div>

        {/* Categories */}
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Topics Covered</p>
          <div className="flex flex-wrap gap-2">
            {['Road Signs', 'Speed Limits', 'Traffic Signals', 'Junctions', 'Roundabouts', 'Pedestrian Crossings', 'Motorways', 'Lane Discipline', 'Hazard Awareness', 'Safe Driving', 'Vehicle Safety', 'Emergency Situations', 'Weather Conditions', 'Parking Rules', 'Highway Code'].map((cat) => (
              <span
                key={cat}
                className="px-2.5 py-1 rounded-full text-xs font-medium"
                style={{ background: '#F1F5F9', color: '#475569', border: '1px solid #E2E8F0' }}
              >
                {cat}
              </span>
            ))}
          </div>
        </div>

        <button
          onClick={onStart}
          className="btn-primary w-full text-sm py-4 rounded-xl"
          style={{ boxShadow: '0 4px 20px rgba(30,64,175,0.35)' }}
          aria-label="Start the 50-question UK driving theory quiz"
        >
          Start Quiz
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ── Fisher-Yates shuffle ───────────────────────────────────────────────────────
function shuffleArray<T>(array: T[]): T[] {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}
// ── END shuffle ────────────────────────────────────────────────────────────────

export default function QuizEngine() {
  const router = useRouter();
  const [started, setStarted] = useState(false);
  const [showCandidateForm, setShowCandidateForm] = useState(false);
  const [shuffledQuestions, setShuffledQuestions] = useState<QuizQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [answers, setAnswers] = useState<AnswerRecord[]>([]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [activeMilestone, setActiveMilestone] = useState<number | null>(null);
  const questionRef = useRef<HTMLDivElement>(null);

  const currentQuestion: QuizQuestion = shuffledQuestions[currentIndex] ?? QUIZ_QUESTIONS[currentIndex];
  const totalQuestions = shuffledQuestions.length || QUIZ_QUESTIONS.length;
  const progressPercent = Math.round(((currentIndex) / totalQuestions) * 100);

  // Scroll to top on question change
  useEffect(() => {
    if (started) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentIndex, started]);

  const handleSelectOption = useCallback((optionId: string) => {
    if (hasAnswered) return;

    const isCorrect = optionId === currentQuestion.correctOptionId;
    setSelectedOptionId(optionId);
    setHasAnswered(true);

    if (isCorrect) {
      setScore((prev) => prev + 1);
    }

    setAnswers((prev) => [
      ...prev,
      {
        questionId: currentQuestion.id,
        selectedOptionId: optionId,
        isCorrect,
        category: currentQuestion.category,
      },
    ]);
  }, [hasAnswered, currentQuestion]);

  const getOptionState = (optionId: string): AnswerState => {
    if (!hasAnswered) {
      return selectedOptionId === optionId ? 'selected' : 'default';
    }
    if (optionId === currentQuestion.correctOptionId) return 'correct';
    if (optionId === selectedOptionId && optionId !== currentQuestion.correctOptionId) return 'incorrect';
    return 'disabled';
  };

  const handleNext = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);

    const nextQuestionNumber = currentIndex + 1; // 0-indexed, so next question number = currentIndex + 1
    const isLastQuestion = currentIndex === totalQuestions - 1;

    // Check milestone (milestones are at question counts 10, 20, 30, 40, 50)
    const completedCount = currentIndex + 1; // questions completed after answering this one
    if (MILESTONES.includes(completedCount)) {
      setActiveMilestone(completedCount);
    }

    if (isLastQuestion) {
      const finalScore = score + (selectedOptionId === currentQuestion.correctOptionId ? 0 : 0);
      const resultsPayload = {
        score,
        totalQuestions,
        passmark: PASS_MARK,
        answers,
        completedAt: new Date().toISOString(),
      };
      sessionStorage.setItem('drivetheory_results', JSON.stringify(resultsPayload));

      // Delay navigation slightly to show milestone banner if it's Q50
      setTimeout(() => {
        router.push('/results-screen');
      }, completedCount === 50 ? 2000 : 300);
      return;
    }

    setTimeout(() => {
      setCurrentIndex((prev) => prev + 1);
      setSelectedOptionId(null);
      setHasAnswered(false);
      setIsTransitioning(false);
    }, 300);
  }, [isTransitioning, currentIndex, totalQuestions, answers, score, selectedOptionId, currentQuestion, router]);

  if (!started) {
    if (showCandidateForm) {
      return (
        <CandidateForm
          onSubmit={(firstName, lastName) => {
            sessionStorage.setItem('drivetheory_candidate', JSON.stringify({ firstName, lastName }));
            setShuffledQuestions(shuffleArray(QUIZ_QUESTIONS));
            setStarted(true);
          }}
        />
      );
    }
    return <QuizIntro onStart={() => setShowCandidateForm(true)} />;
  }

  const correctAnswerText = currentQuestion.options.find(
    (o) => o.id === currentQuestion.correctOptionId
  )?.text ?? '';

  const isLastQuestion = currentIndex === totalQuestions - 1;

  return (
    <div className="min-h-screen pt-16" style={{ background: '#F8F9FB' }}>
      {/* Milestone banner */}
      {activeMilestone !== null && (
        <MilestoneBanner
          milestone={activeMilestone}
          onDismiss={() => setActiveMilestone(null)}
        />
      )}

      <QuizHeader
        currentQuestion={currentIndex + 1}
        totalQuestions={totalQuestions}
        score={score}
        progressPercent={progressPercent}
        category={currentQuestion.category}
      />

      {/* Main quiz area */}
      <main
        className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14"
        aria-label="Quiz question area"
      >
        {/* Live stats bar */}
        <div
          className="flex items-center justify-between mb-6 px-4 py-3 rounded-xl"
          style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', boxShadow: '0 1px 8px rgba(15,23,42,0.05)' }}
          aria-label="Live quiz statistics"
        >
          <div className="flex items-center gap-1.5 text-xs">
            <span style={{ color: '#94A3B8' }}>Score:</span>
            <span className="font-mono-nums font-bold" style={{ color: '#D97706' }}>{score}</span>
            <span style={{ color: '#CBD5E1' }}>/</span>
            <span className="font-mono-nums" style={{ color: '#64748B' }}>{currentIndex + 1}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <span style={{ color: '#94A3B8' }}>Remaining:</span>
            <span className="font-mono-nums font-bold" style={{ color: '#1E40AF' }}>{totalQuestions - currentIndex}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <span style={{ color: '#94A3B8' }}>Pass mark:</span>
            <span className="font-mono-nums font-bold" style={{ color: '#059669' }}>{PASS_MARK}/50</span>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-xs">
            <span style={{ color: '#94A3B8' }}>Progress:</span>
            <span className="font-mono-nums font-bold" style={{ color: '#475569' }}>{progressPercent}%</span>
          </div>
        </div>

        {/* Question card */}
        <div
          ref={questionRef}
          key={`question-card-${currentQuestion.id}`}
          className="fade-in-up"
          style={{ opacity: 0, animationFillMode: 'forwards' }}
        >
          {/* Category + difficulty row */}
          <div className="flex items-center gap-3 mb-5">
            <span className="category-tag">{currentQuestion.category}</span>
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                currentQuestion.difficulty === 'foundation' ?'bg-success/10 text-success-light border-success/20'
                  : currentQuestion.difficulty === 'standard' ?'bg-primary/10 text-primary-light border-primary/20' :'bg-warning/10 text-warning-light border-warning/20'
              }`}
            >
              {currentQuestion.difficulty === 'foundation' ? 'Foundation' : currentQuestion.difficulty === 'standard' ? 'Standard' : 'Advanced'}
            </span>
          </div>

          {/* Question text */}
          <div
            className="rounded-2xl p-6 sm:p-8 mb-6"
            style={{
              background: '#FFFFFF',
              border: '1px solid #E2E8F0',
              boxShadow: '0 2px 16px rgba(15,23,42,0.07)',
            }}
          >
            <div className="flex items-start gap-4">
              <div
                className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center font-mono-nums font-bold text-sm"
                style={{ background: 'rgba(30,64,175,0.08)', color: '#1E40AF', border: '1px solid rgba(30,64,175,0.15)' }}
                aria-hidden="true"
              >
                {String(currentIndex + 1).padStart(2, '0')}
              </div>
              <h2 className="text-lg sm:text-xl font-semibold leading-relaxed flex-1" style={{ color: '#0F172A' }}>
                {currentQuestion.question}
              </h2>
            </div>
          </div>

          {/* Answer options */}
          <div
            className="flex flex-col gap-3 mb-6"
            role="group"
            aria-label="Answer options"
          >
            {currentQuestion.options.map((option, index) => (
              <div
                key={option.id}
                className="fade-in-up"
                style={{
                  opacity: 0,
                  animationFillMode: 'forwards',
                  animationDelay: `${index * 0.08}s`,
                }}
              >
                <AnswerCard
                  optionId={option.id}
                  text={option.text}
                  label={OPTION_LABELS[index]}
                  state={getOptionState(option.id)}
                  onSelect={handleSelectOption}
                  disabled={hasAnswered}
                />
                {/* Inline feedback: show directly below the selected option */}
                {hasAnswered && selectedOptionId === option.id && (
                  <div className="mt-3">
                    <ExplanationPanel
                      isCorrect={selectedOptionId === currentQuestion.correctOptionId}
                      explanation={currentQuestion.explanation}
                      ruleReference={currentQuestion.ruleReference}
                      learningTakeaway={currentQuestion.learningTakeaway}
                      correctAnswerText={correctAnswerText}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Next / Finish button */}
          {hasAnswered && (
            <div className="flex justify-end fade-in" style={{ opacity: 0, animationFillMode: 'forwards' }}>
              <button
                onClick={handleNext}
                disabled={isTransitioning}
                className="btn-primary text-sm px-8 py-3.5 rounded-xl shadow-glow-blue disabled:opacity-60 disabled:cursor-not-allowed"
                aria-label={isLastQuestion ? 'Finish quiz and see results' : 'Continue to next question'}
              >
                {isTransitioning ? (
                  <svg className="animate-spin" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <circle cx="8" cy="8" r="6" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
                    <path d="M8 2a6 6 0 016 6" stroke="white" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                ) : (
                  <>
                    {isLastQuestion ? 'View My Results' : 'Next Question'}
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Hint when not answered */}
          {!hasAnswered && (
            <p className="text-center text-xs text-muted-foreground mt-4" aria-live="polite">
              Select an answer to continue
            </p>
          )}
        </div>

        {/* Bottom question dots */}
        <div className="flex items-center justify-center gap-1 mt-10 flex-wrap" aria-hidden="true">
          {QUIZ_QUESTIONS.map((q, i) => {
            const answered = answers.find((a) => a.questionId === q.id);
            const isCurrent = i === currentIndex;
            return (
              <div
                key={`dot-${q.id}`}
                className="rounded-full transition-all duration-300"
                style={{
                  width: isCurrent ? '8px' : '6px',
                  height: isCurrent ? '8px' : '6px',
                  background: answered
                    ? answered.isCorrect
                      ? '#059669' :'#DC2626'
                    : isCurrent
                    ? '#1E40AF' :'#E2E8F0',
                  boxShadow: isCurrent ? '0 0 6px rgba(30,64,175,0.5)' : 'none',
                }}
              />
            );
          })}
        </div>

        {/* Milestone progress indicators */}
        <div className="flex items-center justify-center gap-4 mt-6" aria-label="Quiz milestones">
          {MILESTONES.map((m) => {
            const reached = currentIndex + 1 > m;
            const isCurrent = currentIndex + 1 === m;
            return (
              <div key={`ms-${m}`} className="flex flex-col items-center gap-1">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-500"
                  style={{
                    background: reached
                      ? 'linear-gradient(135deg, #1E40AF, #3B82F6)'
                      : isCurrent
                      ? 'rgba(30,64,175,0.15)'
                      : '#F1F5F9',
                    color: reached ? '#FFFFFF' : isCurrent ? '#1E40AF' : '#94A3B8',
                    border: isCurrent ? '2px solid #1E40AF' : '2px solid transparent',
                    boxShadow: reached ? '0 2px 8px rgba(30,64,175,0.3)' : 'none',
                  }}
                  aria-label={`Milestone: ${m} questions ${reached ? 'completed' : 'remaining'}`}
                >
                  {reached ? (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                      <path d="M2.5 6l2.5 2.5 4.5-4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    m
                  )}
                </div>
                <span className="text-xs" style={{ color: reached ? '#1E40AF' : '#CBD5E1' }}>Q{m}</span>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}